import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Divider,
  Icon,
  Flex,
  IconButton,
  Badge,
  Input,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import {
  FaCalendarAlt,
  FaClock,
  FaTruck,
  FaEdit,
} from 'react-icons/fa';
import { format, parse } from 'date-fns';
import { getCartSubtotal, getAddonsTotal, getBilledWeight, billedQuantity } from './cartUtils';
import MembershipOfferBanner from './MembershipOfferBanner';

const to12h = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (Number.isNaN(h)) return hhmm;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${suffix}` : `${h12} ${suffix}`;
};

const slotLabel = (slot) => {
  const parts = String(slot || '').split(' - ');
  return parts.length === 2 ? `${to12h(parts[0])} – ${to12h(parts[1])}` : slot;
};

/**
 * UnifiedReviewPage — Review step for the unified cart order flow.
 *
 * Displays all cart items (per-pound and per-piece) with line totals,
 * a grand total breakdown (subtotal, discount, tax, tip), schedule summary,
 * and action buttons (delete item, edit services, place order).
 */
export default function UnifiedReviewPage({
  cart,
  dispatch,
  laundryId,
  customerId,
  address,
  dropoffService,
  pickupDate,
  pickupTime,
  dropoffDate,
  dropoffTime,
  dryCleanDropoffDate = "",
  dropoffDateLabel = "Dropoff",
  customerTimeSelectionEnabled = true,
  tip,
  setTip,
  taxRate = 0,
  promoCode,
  giftCardCode,
  promoDescriptionMessage,
  frequency,
  subscriptionDiscount = 0,
  selectedAddons = [],
  onPlaceOrder,
  onEdit,
  orderProcessing,
}) {
  const items = cart.items || [];
  // Subtotal = services/products + selected add-ons (per-pound priced on the
  // order's billed weight). Must match handlePlaceOrder so what the customer
  // sees equals what is charged (Phase 2c).
  const billedWeight = getBilledWeight(items);
  const addonsTotal = getAddonsTotal(selectedAddons, billedWeight, items);
  // Active membership (null = non-member). Declared here so bedding-discount +
  // prepaid-pounds pricing below can use it; loaded by the effect further down.
  const [membership, setMembership] = useState(null);
  // Member bedding discount % (0 for non-members) — applied to bedding lines in
  // the subtotal, matching the server.
  const memberBeddingPct = membership
    ? (Number(membership.beddingDiscountPct ?? membership.bedding_discount_pct ?? 0) || 0)
    : 0;
  const subtotal = getCartSubtotal(items, memberBeddingPct) + addonsTotal;
  // Full-price subtotal (bedding at full price, no member discount) so a member
  // can see WHY the total is lower: full Subtotal + an explicit discount line.
  const subtotalFull = getCartSubtotal(items, 0) + addonsTotal;
  // Dollar amount saved on bedding due to the member's plan discount.
  const beddingDiscountAmount = Math.round((subtotalFull - subtotal) * 100) / 100;

  // ── Membership (prepaid pounds) ──────────────────────────────────────
  // Covered per-pound pounds cost $0; overage bills at the plan rate. Only
  // per-pound (inputWeight) NON-bedding services draw from the allotment.
  // Reduces the taxable/grand total AND what's charged, matching the server.
  // Fetched by (laundryId, customerId); null for non-members → savings 0.
  useEffect(() => {
    if (!laundryId || !customerId) { setMembership(null); return; }
    let alive = true;
    const base = process.env.REACT_APP_AWS_API_URL || '';
    axios.get(`${base}/api/membership/plans`, { params: { laundryId, customerId } })
      .then((res) => { if (alive) setMembership(res.data?.membership || null); })
      .catch(() => { if (alive) setMembership(null); });
    return () => { alive = false; };
  }, [laundryId, customerId]);

  const membershipInfo = (() => {
    const none = { coveredLb: 0, overageLb: 0, savings: 0, overageRate: 0, overageNote: '' };
    if (!membership) return none;
    const balance = Number(membership.balanceLb ?? membership.balance_lb ?? 0) || 0;
    const overageRate = Number(membership.overageRate ?? membership.overage_rate ?? 0) || 0;
    const overageMode = (membership.overageMode ?? membership.overage_mode ?? 'charge');
    const overageTiming = (membership.overageTiming ?? membership.overage_timing ?? 'at_order');
    let perPoundDollars = 0;
    let billed = 0;
    items.forEach((it) => {
      if (it.inputWeight !== true || it.isBedding) return;
      const q = billedQuantity(it);
      perPoundDollars += (it.price || 0) * q;
      billed += q;
    });
    perPoundDollars = Math.round(perPoundDollars * 100) / 100;
    if (perPoundDollars <= 0 || billed <= 0) return none;
    const covered = Math.min(billed, balance);
    const overageLb = Math.max(0, billed - covered);
    // Same rule as the server's compute_membership_credit: covered pounds are
    // free; overage pounds bill at what the member pays (plan rate at_order,
    // $0 on monthly invoice or unlimited); block mode keeps the shop rate.
    let savings = perPoundDollars * (covered / billed);
    if (overageLb > 0 && overageMode !== 'block_over_allotment') {
      let memberCharge = 0;
      if (overageMode !== 'unlimited_no_overage' && overageTiming === 'at_order') {
        memberCharge = Math.round(overageLb * overageRate * 100) / 100;
      }
      savings += Math.max(0, perPoundDollars * (overageLb / billed) - memberCharge);
    }
    savings = Math.round(Math.max(0, Math.min(savings, perPoundDollars)) * 100) / 100;
    let overageNote = '';
    if (overageLb > 0) {
      if (overageMode === 'unlimited_no_overage') overageNote = `${overageLb} lb over — included`;
      else if (overageMode === 'block_over_allotment') overageNote = `${overageLb} lb over allotment`;
      else overageNote = `${overageLb} lb @ $${overageRate}/lb`;
    }
    return { coveredLb: covered, overageLb, savings, overageRate, overageNote };
  })();
  const membershipSavings = membershipInfo.savings;

  // Discount: subscription discount is a percentage of subtotal
  const discountAmount = subscriptionDiscount > 0
    ? subtotal * (subscriptionDiscount / 100)
    : 0;

  // Tax computed on (subtotal - discount - membership savings). Covered pounds
  // are free, so they are not taxable — only the member's overage remains.
  const taxableAmount = Math.max(0, subtotal - discountAmount - membershipSavings);
  const tax = taxRate > 0 ? taxableAmount * (taxRate / 100) : 0;

  // Tip. A percentage is on the wash after a promo, and before membership
  // credit. Membership can cover the whole order; the tip is still that
  // percent of the wash, not a percent of zero.
  const tipBase = Math.max(0, subtotal - discountAmount);
  const tipAmount = tip?.tipType === 'percentage'
    ? Math.round(tipBase * ((parseFloat(tip?.tipPercentage) || 0) / 100) * 100) / 100
    : (parseFloat(tip?.tipAmount || '0') || 0);

  // Keep the shared tip state in sync so the placed order (handlePlaceOrder)
  // sends the computed dollar amount, not a stale $0.
  useEffect(() => {
    if (tip?.tipType === 'percentage') {
      const computed = (Math.round(tipBase * ((parseFloat(tip?.tipPercentage) || 0) / 100) * 100) / 100).toFixed(2);
      if (computed !== (tip?.tipAmount ?? '').toString() && typeof setTip === 'function') {
        setTip((prev) => ({ ...prev, tipAmount: computed }));
      }
    }
  }, [tip?.tipType, tip?.tipPercentage, tipBase, tip?.tipAmount, setTip]);

  // Delivery fee (Phase 3). Quoted from the server so the customer sees the fee
  // as its own line before paying. The server is authoritative — it recomputes
  // the same fee at order create. When the tenant is on mode 'none' (or the
  // dropoff leg is Uber, which prices itself) the quote returns applies:false
  // and nothing is shown. Fails silently to $0 so it never blocks the flow.
  const [deliveryFeeInfo, setDeliveryFeeInfo] = useState({ applies: false, fee: 0, distanceMi: null });
  useEffect(() => {
    if (!laundryId) return;
    let active = true;
    const base = process.env.REACT_APP_AWS_API_URL || '';
    axios.get(`${base}/api/customer/quote-delivery-fee`, {
      params: { laundryId, address: address || '', dropoffService: dropoffService || 'LaundryDriver' },
    }).then((res) => {
      const d = res?.data || {};
      if (active) {
        setDeliveryFeeInfo({
          applies: !!d.applies,
          fee: parseFloat(d.fee || 0) || 0,
          distanceMi: d.distanceMi != null ? parseFloat(d.distanceMi) : null,
        });
      }
    }).catch(() => {
      if (active) setDeliveryFeeInfo({ applies: false, fee: 0, distanceMi: null });
    });
    return () => { active = false; };
  }, [laundryId, address, dropoffService]);

  const deliveryFee = deliveryFeeInfo.applies ? (deliveryFeeInfo.fee || 0) : 0;

  // Grand total (delivery fee folded in after tax + tip, matching the server).
  const grandTotal = taxableAmount + tax + tipAmount + deliveryFee;

  // Gift card amount, looked up without redeeming. The order applies it when
  // the customer places the order; the cart has to show that same dollar amount.
  const [giftCard, setGiftCard] = useState(null);
  useEffect(() => {
    const code = (giftCardCode || '').trim();
    if (!code || !laundryId) { setGiftCard(null); return undefined; }
    let alive = true;
    const base = process.env.REACT_APP_AWS_API_URL || '';
    const timer = setTimeout(() => {
      axios.post(`${base}/api/customer/gift-cards/preview`, { laundryId, code })
        .then((res) => {
          if (!alive) return;
          const data = res?.data || {};
          if (data.status === 'success') {
            setGiftCard({
              amount: Number(data.amount) || 0,
              accountBalance: Number(data.accountBalance) || 0,
              code: data.code || code,
              note: data.note || '',
            });
          } else {
            setGiftCard({ error: data.message || "That gift card can't be used." });
          }
        })
        .catch(() => { if (alive) setGiftCard({ error: "We couldn't check that gift card. Try again." }); });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [giftCardCode, laundryId]);

  const giftCardAmount = giftCard?.amount > 0 ? giftCard.amount : 0;
  const accountBalance = giftCard?.accountBalance > 0 ? giftCard.accountBalance : 0;
  const orderTotal = Math.round(grandTotal * 100) / 100;
  const available = Math.round((giftCardAmount + accountBalance) * 100) / 100;
  const giftApplied = Math.min(available, orderTotal);
  const fromCard = Math.min(giftCardAmount, giftApplied);
  const fromBalance = Math.round((giftApplied - fromCard) * 100) / 100;
  const amountDue = Math.max(0, Math.round((orderTotal - giftApplied) * 100) / 100);
  const giftRemaining = Math.max(0, Math.round((available - giftApplied) * 100) / 100);

  // Check if any per-pound items exist
  const hasPerPoundItems = items.some(item => item.inputWeight === true);

  // Format dates for display
  const formattedPickupDate = pickupDate
    ? format(parse(pickupDate, 'yyyy-MM-dd', new Date()), 'MMMM d, yyyy')
    : '';
  const formattedDropoffDate = dropoffDate
    ? format(parse(dropoffDate, 'yyyy-MM-dd', new Date()), 'MMMM d, yyyy')
    : '';
  const formattedDryCleanDate = dryCleanDropoffDate
    ? format(parse(dryCleanDropoffDate, 'yyyy-MM-dd', new Date()), 'MMMM d, yyyy')
    : '';

  const handleRemoveItem = (serviceId) => {
    dispatch({ type: 'REMOVE_ITEM', serviceId });
  };

  return (
    <VStack spacing={4} align="stretch" w="100%" maxW="500px" mx="auto" py={2}>
      <Heading size={{ base: 'md', md: 'lg' }} textAlign="center" color="gray.800">
        Review Your Order
      </Heading>

      {/* Items card */}
      <Box
        bg="white"
        borderRadius="2xl"
        p={{ base: 4, md: 6 }}
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.100"
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="sm" color="gray.700">
            Order Items
          </Heading>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="blue"
            leftIcon={<Icon as={FaEdit} />}
            onClick={onEdit}
            aria-label="Edit Services"
          >
            Edit Services
          </Button>
        </Flex>

        {items.length === 0 ? (
          <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
            Your cart is empty.
          </Text>
        ) : (
          <VStack spacing={3} align="stretch" divider={<Divider />}>
            {items.map((item) => {
              const billedQty = billedQuantity(item);
              const lineTotal = billedQty * item.price;
              const wasFloored = item.inputWeight && billedQty > (item.quantity || 0);
              return (
                <Flex key={item.serviceId} justify="space-between" align="center">
                  <VStack align="flex-start" spacing={0} flex="1">
                    <Text fontWeight="600" fontSize="sm" color="gray.800">
                      {item.serviceName}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {item.isBedding
                        ? `${item.quantity} × bedding (1st $${item.price.toFixed(2)}, each add'l $${Number(item.beddingAdditionalPrice ?? 20).toFixed(2)})`
                        : item.inputWeight
                        ? `${billedQty} lbs × $${item.price.toFixed(2)}/lb = $${lineTotal.toFixed(2)}`
                        : `${item.quantity} × $${item.price.toFixed(2)} = $${lineTotal.toFixed(2)}`}
                    </Text>
                    {wasFloored && (
                      <Text fontSize="xs" color="orange.600">
                        Billed at {billedQty} lb minimum (you entered {item.quantity} lb)
                      </Text>
                    )}
                    {item.isBedding && (
                      <Text fontSize="xs" color="purple.600">
                        Multi-item bedding pricing applied — see subtotal
                      </Text>
                    )}
                  </VStack>
                  <HStack spacing={2}>
                    {!item.isBedding && (
                      <Text fontWeight="bold" fontSize="sm" color="blue.600">
                        ${lineTotal.toFixed(2)}
                      </Text>
                    )}
                    <IconButton
                      icon={<CloseIcon boxSize={2.5} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      aria-label={`Remove ${item.serviceName}`}
                      onClick={() => handleRemoveItem(item.serviceId)}
                    />
                  </HStack>
                </Flex>
              );
            })}
          </VStack>
        )}
      </Box>

      {/* Schedule card */}
      <Box
        bg="white"
        borderRadius="2xl"
        p={{ base: 4, md: 6 }}
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.100"
      >
        <Heading size="sm" mb={3} color="gray.700">Schedule</Heading>
        <VStack spacing={3} align="stretch">
          {/* Pickup */}
          <Flex justify="space-between" align="center">
            <HStack spacing={2}>
              <Icon as={FaCalendarAlt} color="blue.400" boxSize={4} />
              <Text fontSize="sm" fontWeight="600">Pickup</Text>
            </HStack>
            <VStack align="flex-end" spacing={0}>
              <Text fontSize="sm" color="gray.700">{formattedPickupDate}</Text>
              {customerTimeSelectionEnabled && pickupTime && (
                <HStack spacing={1}>
                  <Icon as={FaClock} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.500">{slotLabel(pickupTime)}</Text>
                </HStack>
              )}
            </VStack>
          </Flex>

          <Divider />

          {/* Dropoff */}
          <Flex justify="space-between" align="center">
            <HStack spacing={2}>
              <Icon as={FaTruck} color="blue.400" boxSize={4} />
              <Text fontSize="sm" fontWeight="600">{dropoffDateLabel}</Text>
            </HStack>
            <VStack align="flex-end" spacing={0}>
              <Text fontSize="sm" color="gray.700">{formattedDropoffDate}</Text>
              {customerTimeSelectionEnabled && dropoffTime && (
                <HStack spacing={1}>
                  <Icon as={FaClock} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.500">{slotLabel(dropoffTime)}</Text>
                </HStack>
              )}
            </VStack>
          </Flex>
          {formattedDryCleanDate && (
            <>
              <Divider />
              <Flex justify="space-between" align="center">
                <HStack spacing={2}>
                  <Icon as={FaTruck} color="blue.400" boxSize={4} />
                  <Text fontSize="sm" fontWeight="600">Dry cleaning back</Text>
                </HStack>
                <Text fontSize="sm" color="gray.700">{formattedDryCleanDate}</Text>
              </Flex>
            </>
          )}
        </VStack>
      </Box>

      {/* Frequency / Promo info */}
      {(frequency || promoCode || giftCardCode) && (
        <Box
          bg="white"
          borderRadius="2xl"
          p={{ base: 4, md: 6 }}
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.100"
        >
          {frequency && (
            <HStack spacing={2} mb={promoCode ? 2 : 0}>
              <Text fontSize="sm" fontWeight="600" color="gray.700">Frequency:</Text>
              <Badge colorScheme="purple" borderRadius="full" px={2}>
                {frequency}
              </Badge>
            </HStack>
          )}
          {giftCardCode && (
            <HStack spacing={2} mt={promoCode || frequency ? 2 : 0} align="flex-start">
              <Text fontSize="sm" fontWeight="600" color="gray.700">Gift card:</Text>
              <VStack align="flex-start" spacing={0}>
                <Badge colorScheme="pink" borderRadius="full" px={2}>
                  {giftCard?.code || giftCardCode}{giftCardAmount > 0 ? ` · $${giftCardAmount.toFixed(2)}` : ''}
                </Badge>
                {giftCard?.note && (
                  <Text fontSize="xs" color="gray.600" mt={1}>{giftCard.note}</Text>
                )}
                {giftCard?.error && (
                  <Text fontSize="xs" color="red.500" mt={1}>{giftCard.error}</Text>
                )}
              </VStack>
            </HStack>
          )}
          {promoCode && (
            <VStack spacing={1} align="flex-start">
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="600" color="gray.700">Promo:</Text>
                <Badge colorScheme="green" borderRadius="full" px={2}>
                  {promoCode}
                </Badge>
              </HStack>
              {promoDescriptionMessage && (
                <Text fontSize="xs" color="green.600">{promoDescriptionMessage}</Text>
              )}
              <Text fontSize="xs" color="gray.500" fontStyle="italic">
                * Discount will be applied after your laundry is delivered
              </Text>
            </VStack>
          )}
        </Box>
      )}

      {/* Selected add-ons (Phase 2c) */}
      {selectedAddons.length > 0 && (
        <Box
          bg="gray.50"
          borderRadius="xl"
          p={3}
          border="1px solid"
          borderColor="gray.100"
        >
          <Text fontWeight="600" fontSize="sm" color="gray.700" mb={1}>Add-Ons</Text>
          {selectedAddons.flatMap((a) => {
            if (a.pricingBasis === 'per_bag') {
              const matches = (a.bagPrices || []).map((p) => {
                const name = String(p.serviceName || '').trim().toLowerCase();
                const qty = items.reduce((count, item) => {
                  const sameId = p.serviceId != null && String(item.serviceId) === String(p.serviceId);
                  const sameName = name && String(item.serviceName || '').trim().toLowerCase() === name;
                  return sameId || sameName ? count + (parseFloat(item.quantity) || 0) : count;
                }, 0);
                return { ...p, qty };
              }).filter((p) => p.qty > 0);
              if (matches.length === 0) {
                return [(
                  <Flex key={a.addonId} justify="space-between" align="center" fontSize="sm" color="gray.600">
                    <Text>{a.addonName} (added when this order includes a priced bag)</Text>
                    <Text>—</Text>
                  </Flex>
                )];
              }
              return matches.map((p) => {
                const unit = parseFloat(p.unitPrice) || 0;
                return (
                  <Flex key={`${a.addonId}-${p.serviceId || p.serviceName}`} justify="space-between" align="center" fontSize="sm" color="gray.600">
                    <Text>{a.addonName} — {p.serviceName} (×{p.qty})</Text>
                    <Text>${(unit * p.qty).toFixed(2)}</Text>
                  </Flex>
                );
              });
            }
            const unit = parseFloat(a.unitPrice) || 0;
            const pendingWeight = a.pricingBasis === 'per_pound' && !(billedWeight > 0);
            const qty = a.pricingBasis === 'per_pound' ? billedWeight : (parseFloat(a.quantity) || 0);
            const amount = unit * qty;
            return [(
              <Flex key={a.addonId} justify="space-between" align="center" fontSize="sm" color="gray.600">
                <Text>
                  {a.addonName}
                  {pendingWeight
                    ? ` ($${unit.toFixed(2)}/lb after we weigh your laundry)`
                    : a.pricingBasis === 'per_pound'
                    ? ` (${billedWeight} lb × $${unit.toFixed(2)})`
                    : ` (×${qty})`}
                </Text>
                <Text>{pendingWeight ? '—' : `$${amount.toFixed(2)}`}</Text>
              </Flex>
            )];
          })}
        </Box>
      )}

      {/* Tip selector — customer can choose a preset percentage or a custom amount */}
      <Box
        bg="white"
        borderRadius="2xl"
        p={4}
        border="1px solid"
        borderColor="gray.200"
      >
        <Text fontWeight="600" color="gray.700" mb={2}>Add a tip</Text>
        <Wrap spacing={2}>
          {[5, 10, 15, 20, 25].map((pct) => {
            const isSelected = tip?.tipType === 'percentage'
              && parseFloat(tip?.tipPercentage) === pct;
            return (
              <WrapItem key={pct}>
                <Button
                  size="sm"
                  borderRadius="lg"
                  variant={isSelected ? 'solid' : 'outline'}
                  colorScheme="blue"
                  onClick={() => setTip((prev) => ({
                    ...prev,
                    tipType: 'percentage',
                    tipPercentage: pct,
                    // tipAmount recomputed by the effect above from the taxable subtotal
                  }))}
                >
                  {pct}%
                </Button>
              </WrapItem>
            );
          })}
          <WrapItem>
            <Button
              size="sm"
              borderRadius="lg"
              variant={tip?.tipType === 'custom' ? 'solid' : 'outline'}
              colorScheme="blue"
              onClick={() => setTip((prev) => ({
                ...prev,
                tipType: 'custom',
                tipPercentage: 0,
                tipAmount: prev?.tipType === 'custom' ? prev.tipAmount : '',
              }))}
            >
              Custom
            </Button>
          </WrapItem>
          <WrapItem>
            <Button
              size="sm"
              borderRadius="lg"
              variant={tip?.tipType === 'noTip' ? 'solid' : 'outline'}
              colorScheme="gray"
              onClick={() => setTip((prev) => ({
                ...prev,
                tipType: 'noTip',
                tipPercentage: 0,
                tipAmount: '0.00',
              }))}
            >
              No Tip
            </Button>
          </WrapItem>
        </Wrap>

        {tip?.tipType === 'custom' && (
          <HStack mt={3} maxW="200px">
            <Text color="gray.600">$</Text>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={tip?.tipAmount ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setTip((prev) => ({ ...prev, tipType: 'custom', tipPercentage: 0, tipAmount: v }));
              }}
              size="sm"
              borderRadius="lg"
            />
          </HStack>
        )}
      </Box>

      {/* Membership offer (primary in-flow door) — non-members only, shown at
          the price reveal so the pitch is against this order's real cost. */}
      <MembershipOfferBanner laundryId={laundryId} customerId={customerId} subtotal={subtotal} estWeightLb={billedWeight} />

      {/* Grand total section */}
      <Box
        bg="blue.50"
        borderRadius="2xl"
        p={4}
        border="1px solid"
        borderColor="blue.100"
      >
        <Flex justify="space-between" align="center">
          <Text fontWeight="600" color="gray.700">Subtotal</Text>
          <Text color="gray.700">${subtotalFull.toFixed(2)}</Text>
        </Flex>

        {/* Bedding discount — shows the member WHY the total is lower: the full
            bedding price is in Subtotal above, the plan's bedding discount here. */}
        {membership && beddingDiscountAmount > 0 && (
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="sm" color="purple.600" fontWeight="600">
              Bedding discount ({memberBeddingPct}% member)
            </Text>
            <Text fontSize="sm" color="purple.600" fontWeight="600">
              -${beddingDiscountAmount.toFixed(2)}
            </Text>
          </Flex>
        )}

        {discountAmount > 0 && (
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="sm" color="green.600" fontWeight="600">
              Discount ({subscriptionDiscount}%)
            </Text>
            <Text fontSize="sm" color="green.600" fontWeight="600">
              -${discountAmount.toFixed(2)}
            </Text>
          </Flex>
        )}

        {/* Membership savings — prepaid allotment (covered pounds free). One
            plain line that reconciles: Subtotal − savings (− discount) = total. */}
        {membership && membershipSavings > 0 && (
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="sm" color="purple.600" fontWeight="600">
              Membership savings ({membershipInfo.coveredLb} lb covered
              {membershipInfo.overageNote ? `, ${membershipInfo.overageNote}` : ''})
            </Text>
            <Text fontSize="sm" color="purple.600" fontWeight="600">
              -${membershipSavings.toFixed(2)}
            </Text>
          </Flex>
        )}

        {tax > 0 && (
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="sm" color="gray.500">Tax ({taxRate}%)</Text>
            <Text fontSize="sm" color="gray.500">${tax.toFixed(2)}</Text>
          </Flex>
        )}

        {tipAmount > 0 && (
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="sm" color="gray.500">
              Tip{tip?.tipType === 'percentage' && tip?.tipPercentage ? ` (${tip.tipPercentage}%)` : ''}
            </Text>
            <Text fontSize="sm" color="gray.500">${tipAmount.toFixed(2)}</Text>
          </Flex>
        )}

        {deliveryFee > 0 && (
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="sm" color="gray.500">
              Distance-Based Delivery Fee
            </Text>
            <Text fontSize="sm" color="gray.500">${deliveryFee.toFixed(2)}</Text>
          </Flex>
        )}

        {fromCard > 0 && (
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="sm" color="pink.600" fontWeight="600">Gift card</Text>
            <Text fontSize="sm" color="pink.600" fontWeight="600">-${fromCard.toFixed(2)}</Text>
          </Flex>
        )}
        {fromBalance > 0 && (
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="sm" color="green.700" fontWeight="600">Account balance</Text>
            <Text fontSize="sm" color="green.700" fontWeight="600">-${fromBalance.toFixed(2)}</Text>
          </Flex>
        )}

        <Divider my={2} borderColor="blue.200" />

        {giftApplied > 0 && (
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color="gray.600">Grand Total</Text>
            <Text fontSize="sm" color="gray.600">${orderTotal.toFixed(2)}</Text>
          </Flex>
        )}
        <Flex justify="space-between" align="center" mt={giftApplied > 0 ? 1 : 0}>
          <Text fontWeight="bold" fontSize="lg" color="gray.800">
            {giftApplied > 0 ? 'Amount due' : 'Grand Total'}
          </Text>
          <Text fontWeight="bold" fontSize="lg" color="blue.600">
            ${(giftApplied > 0 ? amountDue : grandTotal).toFixed(2)}
          </Text>
        </Flex>
        {giftRemaining > 0 && (
          <Text fontSize="xs" color="gray.500" mt={1}>
            Remaining account balance ${giftRemaining.toFixed(2)}
          </Text>
        )}
        {giftApplied > 0 && amountDue > 0 && (
          <Text fontSize="xs" color="gray.500" mt={1}>
            The gift card does not cover this order. A card on file pays the rest.
          </Text>
        )}
      </Box>

      {/* Per-pound disclaimer */}
      {hasPerPoundItems && (
        <Text fontSize="xs" fontStyle="italic" color="gray.500" px={2}>
          * final price based on actual weight
        </Text>
      )}

      {/* Place Order button */}
      <Button
        colorScheme="blue"
        size="lg"
        borderRadius="xl"
        w="100%"
        onClick={onPlaceOrder}
        isDisabled={items.length === 0}
        isLoading={orderProcessing}
        loadingText="Placing Order..."
        boxShadow="lg"
      >
        Place Order — ${(giftApplied > 0 ? amountDue : grandTotal).toFixed(2)}
      </Button>
    </VStack>
  );
}
